import { Extension, Lexical } from '@37signals/lexxy'
import type { LexicalEditor, NodeKey, NodeMutation, TextNode as LexicalTextNode } from 'lexical'

const { $getNodeByKey, $getSelection, $isRangeSelection, $isTextNode, TextNode } = Lexical

const VARIABLE = /\{\{\s*([\w.]+)\s*\}\}/

function $tokenize (node: LexicalTextNode): void {
  const match = node.isSimpleText() ? node.getTextContent().match(VARIABLE) : null
  if (match?.index === undefined) return

  const end = match.index + match[0].length
  const selection = $getSelection()
  const caretAtEnd = $isRangeSelection(selection) && selection.isCollapsed() && selection.anchor.key === node.getKey() && selection.anchor.offset === end

  const variable = match.index === 0 ? node.splitText(end)[0] : node.splitText(match.index, end)[1]
  variable.setMode('token')
  if (caretAtEnd) variable.selectNext()
}

function decorate (editor: LexicalEditor, mutations: Map<NodeKey, NodeMutation>, keys: string[]): void {
  editor.getEditorState().read(() => {
    for (const [key, mutation] of mutations) {
      const node = $getNodeByKey(key)
      const element = editor.getElementByKey(key)
      const match = $isTextNode(node) && node.isToken() ? node.getTextContent().match(VARIABLE) : null
      if (mutation === 'destroyed' || element === null || match === null) continue

      element.spellcheck = false
      if (keys.includes(match[1])) {
        element.classList.add('rounded-sm', 'bg-primary-100', 'px-1', 'py-0.5', 'text-primary-700')
      } else {
        element.classList.add('underline', 'decoration-red-500', 'decoration-wavy', 'underline-offset-4')
        element.title = 'Unknown variable'
      }
    }
  })
}

export class VariableExtension extends Extension {
  get enabled (): boolean {
    return this.keys.length > 0
  }

  get lexicalExtension (): unknown {
    const { keys } = this

    return this.defineExtension({
      name: 'hoot/variables',
      register: (editor: LexicalEditor) => Lexical.mergeRegister(
        editor.registerNodeTransform(TextNode, $tokenize),
        editor.registerMutationListener(TextNode, mutations => { decorate(editor, mutations, keys) })
      )
    })
  }

  private get keys (): string[] {
    return this.editorConfig.get('variables') as string[]
  }
}
